/** Onboarding wizard (pages/onboarding.tsx). */
export const onboarding = {
  header: {
    title: "Let's get you set up",
    subtitle: "A few questions and we'll build your first working setup.",
  },

  // Business-type option buttons (step 0). Values double as the stored/sent choice.
  businessTypes: {
    cafe: "Café / Coffee shop",
    restaurant: "Restaurant",
    retail: "Retail store",
    gym: "Gym / Fitness",
    salon: "Salon / Spa",
    hotel: "Hotel / Hospitality",
    bar: "Bar / Nightclub",
    office: "Office / Workplace",
    other: "Other",
  },

  // Onboarding-specific one-liners for each channel option (labels come from `channels.*`).
  channels: {
    musicDescription: "Background music playlists",
    adsDescription: "In-store promotional spots",
    announcementsDescription: "Live and scheduled messages",
    videoDescription: "Screens and digital signage",
  },

  // Step indicator titles.
  steps: {
    business: "Business",
    locations: "Locations",
    channels: "Channels",
    music: "Music",
    hours: "Hours",
    plan: "Plan",
  },

  business: {
    title: "What kind of business is this?",
    subtitle:
      "Pick every type that fits. Your first pick is the main type and shapes the starter setup. Extra types broaden the suggested music.",
    primaryBadge: "Primary",
  },

  locations: {
    title: "Where will you play?",
    subtitle: "Add your venues and how many players each needs.",
    nameLabel: "Location name",
    namePlaceholder: "e.g. Downtown Store",
    playersLabel: "Players",
    addAnother: "Add another location",
  },

  channelsStep: {
    title: "Which channels do you need?",
    subtitle: "We'll create a channel of each type you choose.",
    liveBroadcastTitle: "Speak into your control page and your voice plays in the store",
    liveBroadcastDescription:
      "Enable Live Broadcast: live announcements from any browser, straight to your speakers.",
  },

  music: {
    title: "Pick your vibe",
    subtitleWithMusic:
      "Every track is original, owned by TrueSmartTech, and included in your subscription: no takedowns and no extra music-licensing fees from us. Pick genres and we'll build a starter playlist for each.",
    subtitleNoMusic: "Music channel not selected. You can skip this step.",
    errorTitle: "Couldn't load genres",
    errorDescription:
      "Something went wrong while loading the music catalogue. Check your connection and try again.",
    emptyGenres: "No genres in the catalogue yet.",
    noMusicSelected: "No music channel selected.",
  },

  hours: {
    title: "When are you open?",
    subtitle: "We'll schedule playback to follow your opening hours.",
    openingLabel: "Opening time",
    closingLabel: "Closing time",
    offlineSync:
      "Keep a local cache on the desktop and TV player apps so they keep playing if the internet drops. The browser player streams online.",
  },

  provision: {
    ready: "Ready to provision",
    summary: "{locations} location(s), {players} player(s), {channels} channel(s){playlists}.",
    playlists: ", {count} starter playlist(s)",
  },

  trial: {
    title: "Start your free trial",
    description:
      "Your {trialDays}-day free trial starts now, no credit card required. Every feature is included for up to {allowance} player devices. Need more? Add paid licenses anytime from Billing.",
    everythingIncluded: "Everything included",
    priceLead: "After your trial, it's",
    priceAmount: "€49.99 / device / month",
    priceTail:
      "(or €499.99 / year, about two months free). Add a payment method anytime from Billing.",
    startButton: "Start free trial",
  },

  finishButton: "Finish setup",
  skip: "Skip for now",

  toast: {
    workspaceReadyTitle: "Workspace ready",
    workspaceReadyDescription: "Your starter setup has been created.",
    skippedTitle: "Onboarding skipped",
    skippedDescription: "You can configure everything from the dashboard.",
    addLocationTitle: "Add at least one location",
    setupFailedTitle: "Could not complete setup",
    setupFailedDescription: "Try again.",
    skipFailedTitle: "Could not skip onboarding",
    devicesClampedTitle: "Your trial includes 1 device",
    devicesClampedDescription:
      "We set up your first player. Add more devices any time from Fleet once you upgrade.",
    catalogueEmptyTitle: "Music is still loading",
    catalogueEmptyDescription:
      "Your workspace is ready, but the music library isn't indexed yet. Starter stations will fill in shortly.",
  },
} as const;
